/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { DataState } from '../UNSAFE_Collection';
import { List } from '../PRIVATE_List';
import { ListProps } from './List.types';

/**
 * Props specific to non-virtualized ListView
 */
type NonVirtualizedListProps<K, D> = {
  /**
   * The data that is used to render each item in ListView.  If the value is null, then ListView will show nothing
   * if hasMore is false, or loading indicator if hasMore is true.
   */
  data: D[] | null;
  /**
   * A callback function to extract the key which is used to identify the item.
   */
  getRowKey: (data: D) => K;
  /**
   * Whether there are more data available to load.  By default this returns false.
   */
  hasMore?: boolean;
  /**
   * An optional callback function which is invoked when user scrolls to the end of the list.
   */
  onLoadMore?: () => void;
};

/**
 * Props for the ListView Component
 */
export type Props<K, D> = Pick<
  ListProps<K, D>,
  | 'aria-label'
  | 'aria-labelledby'
  | 'children'
  | 'currentKey'
  | 'currentItemVariant'
  | 'gridlines'
  | 'onCurrentKeyChange'
  | 'onItemAction'
  | 'onSelectionChange'
  | 'promotedSection'
  | 'selectedKeys'
  | 'selectionMode'
  | 'viewportConfig'
> &
  NonVirtualizedListProps<K, D>;

/**
 * Component that renders items as a flat list.
 * This component is nom-virtualized and renders all rows provided in the data prop.
 */
export function ListView<K extends string | number, D>(props: Props<K, D>) {
  const dataMetadata = props.data
    ? props.data.map((aData: D) => {
        const key = props.getRowKey(aData);
        return { data: aData, metadata: { key } };
      })
    : null;
  const loadRange = props.onLoadMore ? props.onLoadMore : () => {};
  const dataState = dataMetadata
    ? ({
        offset: 0,
        data: dataMetadata,
        sizePrecision: props.hasMore ? 'atLeast' : 'exact',
        totalSize: dataMetadata.length
      } as DataState<K, D>)
    : null;
  return (
    <List isVirtualized={false} {...props} onLoadRange={loadRange} data={dataState}>
      {props.children}
    </List>
  );
}
